import { ReactNode } from 'react';
import clsx from 'clsx';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';

import ExportIcon from '~/shared/assets/icons/export.svg';
import Button from '~/shared/components/ButtonsV2';
import Typography from '~/shared/components/Typography';

export interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitles?: (string | undefined)[];
  onExport: () => void;
  isExporting?: boolean;
  isExportDisabled?: boolean;
  errorMessage?: string | null;
  children: ReactNode;
}


export default function ExportModal({
  isOpen,
  onClose,
  title = 'Export',
  subtitles,
  onExport,
  isExporting = false,
  isExportDisabled = false,
  errorMessage,
  children,
}: ExportModalProps) {
  const handleClose = () => {
    if (isExporting) return;
    onClose();
  };

  const visibleSubtitles = subtitles?.filter((s): s is string => !!s) ?? [];

  return (
    <Dialog
      open={isOpen}
      as="div"
      className="relative z-[99] focus:outline-none"
      onClose={handleClose}
    >
      <div className="fixed inset-0 p-4 w-screen overflow-y-auto bg-[#00000059] backdrop-blur-[6px]">
        <div className="flex items-center justify-center p-8 xl:p-16 2xl:p-20 h-full">
          <DialogPanel
            transition
            className="flex my-auto max-h-full  w-full max-w-[843px] flex-col rounded-xl bg-white backdrop-blur-2xl duration-300 ease-out overflow-hidden data-closed:transform-[scale(95%)] data-closed:opacity-0"
          >
            <div className="flex flex-col min-h-full p6">
              <div className="pt-6 px-6">
                <div className="py-2.5 shrink-0">
                  <div className="flex flex-col font-family-outfit">
                    <DialogTitle as="h3" className="text-2xl 2xl:text-[28px] leading-[24px] font-[500]">
                      {title}
                    </DialogTitle>
                    {visibleSubtitles.length > 0 && (
                      <div className="flex divide-x-2 divide-gray-400">
                        {visibleSubtitles.map((subtitle, index) => (
                          <Typography
                            key={index}
                            variant="p3"
                            fontWeight={400}
                            color="text-gray-700"
                            className={index > 0 ? 'pl-2' : 'pr-2'}
                          >
                            {subtitle}
                          </Typography>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="pt-0 p-6 flex-1 flex flex-col min-h-0 overflow-y-auto side-panel-scrollbar mb-2">
                <div className="flex flex-col gap-5 py-3">{children}</div>
              </div>

              <div className="mt-auto flex flex-col px-4 py-5 items-center justify-between gap-4 border-t border-primary-70 bg-white z-50">
                <div aria-live="polite" className={errorMessage ? 'min-w-0' : 'sr-only'}>
                  {errorMessage}
                </div>
                <div className="flex justify-between gap-2 w-full ">
                  <Button
                    onClick={handleClose}
                    variant="third"
                    className="px-5"
                    disabled={isExporting}
                    type="button"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={onExport}
                    disabled={isExportDisabled || isExporting}
                    loading={isExporting}
                    type="button"
                    className={clsx('px-5 min-w-[108px]')}
                    leftIcon={<ExportIcon className="size-5 text-white" />}
                  >
                    Export
                  </Button>
                </div>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}

export function ExportSection({
  label,
  children,
  borderBottom = true,
}: {
  label: string;
  children: ReactNode;
  borderBottom?: boolean;
}) {
  return (
    <div className={clsx('flex flex-col gap-2 pb-3', borderBottom && 'border-b border-primary-70')}>
      <Typography variant="H5" fontWeight={500} className="text-primary-900 font-family-outfit">
        {label}
      </Typography>
      <div className="grid grid-cols-2 gap-2">{children}</div>
    </div>
  );
}
